import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { MusicNote } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getRecentPlaylists, getSpotifyPlaylists, SpotifyPlaylist } from '../../services/sonosApi';
import { SURFACE, SURFACE_BORDER } from './sonosUiStyles';

interface SpotifyRecentGridProps {
  speakerId?: number | null;
  onPlay: (pl: SpotifyPlaylist) => void;
  variant?: 'classic' | 'spotify';
}

function mapRecentItem(item: Record<string, unknown>): SpotifyPlaylist {
  return {
    uri: String(item.spotify_playlist_uri || item.uri || ''),
    id: String(item.spotify_playlist_uri || item.uri || item.playlist_id || ''),
    name: String(item.spotify_playlist_name || item.name || item.playlist_name || ''),
    image_url: (item.spotify_playlist_image_url || item.image_url || null) as string | null,
    tracks: null,
    owner: null,
  };
}

async function enrichWithPlaylistImages(items: SpotifyPlaylist[]): Promise<SpotifyPlaylist[]> {
  if (!items.some((i) => !i.image_url)) return items;
  try {
    const pls = await getSpotifyPlaylists();
    const imgByUri = new Map(pls.map((p) => [p.uri, p.image_url]));
    return items.map((item) => ({
      ...item,
      image_url: item.image_url || imgByUri.get(item.uri) || null,
    }));
  } catch {
    return items;
  }
}

const CoverArt: React.FC<{ url: string | null; size: number }> = ({ url, size }) => (
  <Box sx={{
    width: size,
    height: size,
    borderRadius: 1,
    flexShrink: 0,
    background: SURFACE,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}>
    {url ? (
      <Box
        component="img"
        src={url}
        alt=""
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    ) : (
      <MusicNote sx={{ color: '#555', fontSize: size * 0.45 }} />
    )}
  </Box>
);

const SpotifyRecentGrid: React.FC<SpotifyRecentGridProps> = ({
  speakerId, onPlay, variant = 'classic',
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<SpotifyPlaylist[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const raw = await getRecentPlaylists(speakerId ?? undefined);
        let mapped = raw.map((item) => mapRecentItem(item)).filter((pl) => pl.uri && pl.name);
        mapped = await enrichWithPlaylistImages(mapped);
        setItems(mapped);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [speakerId]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} sx={{ color: '#2391FF' }} /></Box>;
  }

  if (items.length === 0) {
    return <Typography sx={{ color: '#999', fontSize: 12 }}>{t('musicSystem.playback.noRecent')}</Typography>;
  }

  if (variant === 'spotify') {
    return (
      <Box sx={{ width: '100%', minWidth: 0 }}>
        {items.map((pl) => (
          <Box
            key={pl.uri}
            onClick={() => onPlay(pl)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.75,
              cursor: 'pointer',
              borderBottom: `1px solid ${SURFACE_BORDER}`,
              minWidth: 0,
            }}
          >
            <CoverArt url={pl.image_url} size={44} />
            <Typography sx={{
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {pl.name}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: 0.75,
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
    }}>
      {items.map((pl) => (
        <Box
          key={pl.uri}
          onClick={() => onPlay(pl)}
          sx={{ backgroundColor: '#2F3235', borderRadius: 1.5, p: 0.5, cursor: 'pointer', minWidth: 0, '&:hover': { backgroundColor: '#3a3d40' } }}
        >
          <CoverArt url={pl.image_url} size={42} />
          <Typography sx={{ color: '#fff', fontSize: 12, mt: 0.35, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {pl.name}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default SpotifyRecentGrid;
